import React, { ComponentType } from 'react';
import { StyleSheet, View, Text } from 'react-native';
import {
  ControlProps,
  isStringControl,
  JsonFormsRendererRegistryEntry,
  RankedTester,
  rankWith,
} from '@jsonforms/core';
import { withJsonFormsControlProps } from '@jsonforms/react';
import { FloatingLabelInput } from 'react-native-floating-label-input';
import Colors from '../../../../constants/Colors';

/**
 * Tester to determine if a control can be used with this renderer.
 */
const tester: RankedTester = rankWith(
  1, // low and positive so that it matches but allows more specific types as well
  isStringControl // built-in matcher for anything that accepts string input
);

/**
 * Base renderer to show a text input control.
 * @param props Control props to be hydrated using `withJsonFormsControlProps`.
 */
const TextControlBase: ComponentType<ControlProps> = (props: ControlProps) => {
  return (
    <View style={styles.inputElement}>
      <View style={styles.smallRow}>
        <View style={styles.labelInputContainer}>
          <Text>
            <Text style={styles.label}>{props.label}</Text>
            <Text style={styles.errorLabel}>{' ' + props.errors}</Text>
          </Text>
          <FloatingLabelInput
            label={
              // don't float the label, instead just show a static label
              ''
            }
            containerStyles={styles.inputContainer}
            inputStyles={{
              ...styles.input,
              ...(props.errors.length > 0 ? styles.errorInput : {}),
            }}
            onChangeText={(text: string) => props.handleChange(props.path, text)}
            value={props.data ?? ''}
          />
        </View>
      </View>
    </View>
  );
};

/**
 * Renderer registry entry for text input control.
 * Bundles the tester and the wrapped control for use with form.
 */
export const TextControl: JsonFormsRendererRegistryEntry = {
  tester,
  renderer: withJsonFormsControlProps(TextControlBase),
};

const styles = StyleSheet.create({
  inputElement: {
    flex: 1,
    paddingRight: 16,
    marginBottom: 16,
  },
  smallRow: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  labelInputContainer: {
    flex: 1,
    marginLeft: 0,
  },
  inputContainer: {
    borderWidth: 0,
    borderBottomRightRadius: 0,
    borderBottomLeftRadius: 0,
    width: 100,
    overflow: 'hidden',
    marginTop: 8,
  },
  input: {
    color: Colors.veryDarkGray,
    fontSize: 16,
    fontFamily: 'roboto-regular',
    borderBottomWidth: 1,
    paddingBottom: 4,
    borderBottomColor: Colors.grayBorder,
  },
  errorInput: {
    borderBottomColor: Colors.brightRed,
  },
  label: {
    color: Colors.veryDarkGray,
  },
  errorLabel: {
    color: Colors.brightRed,
  },
});
